// /customers and /customers/:id. Customers are Autotask companies; Omni stores a
// summary of each from the last sync and decides which dealer owns it. The list
// reads only those summaries (no contacts, no Autotask call); a customer's page
// reads its contacts live. Ops also get the counts that must add up, and a Review
// tab for what the sync could not settle on its own. Each customer's account in the
// products Phoneware resells (mock M3, #1777) shows on its page, and Billing ops and
// Super admins confirm or reject what Omni suggests on the Review tab.
import React, { useEffect, useState } from "react";
import {
  accountNumberSource,
  formatCents,
  quoteTotals,
  type AccountEvidence,
  type AccountEvidenceKind,
  type AccountMatchRecord,
  type AccountProduct,
  type AccountRow,
  type AccountRowAccount,
  type AccountSuggestion,
  type AutotaskOwner,
  type BillingReviewItem,
  type CustomerAccounts,
  type CustomerBillingCandidate,
  type CustomerSummary,
  type CustomersReview,
  type CustomerV2,
  type Dealer,
  type LensUser,
  type Order,
  type Quote,
  type SyncStatus,
} from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { categoryLinkOf, type CategoryStatus } from "../api-customers.js";
import { Icon } from "../icons.js";
import { Link, navigate, rowLink, useLocation } from "../router.js";
import { isNotYours, useLoad, useSession, viewerKind, type Loaded, type ViewerKind } from "../session.js";
import { QUOTE_TYPE, errorText, plural, stamp } from "../format.js";
import { Avatar, Callout, Chip, Crumb, Head, NotYours, QuoteStatusChip, Sheet, useTitle, useToast } from "../ui.js";
import { CreateDealerSheet, LinkCategorySheet } from "./CategorySheets.js";
import { AddContactSheet, AddCustomerSheet, AssignSheet, BillingSheet } from "./CustomerSheets.js";
import { AccountNumber, AccountNumberLegend, BILL_VIA, BillingChip, CategoryName, TypeTag, personName, placeLine, resourceName, suggestDealer, whyText } from "./customer-bits.js";
import { NextMove, orderViewer, stageLabel } from "./Orders.js";
import { useRepNames } from "./Quotes.js";
import { MadeBy } from "./connect-bits.js";
import { RecordActivity } from "./Activity.js";
import { PRODUCT } from "./AccountRows.js";

type TypeFilter = "all" | "customers" | "prospects";
type BillingFilter = "any" | "linked" | "unreviewed" | "none";

/** Ops: the identity the customer counts must hold, as the last sync read them. */
function CountsStrip({ sync, customers }: { sync: SyncStatus | null; customers: CustomerSummary[] | null }): React.ReactElement | null {
  const counts = sync?.customers?.counts ?? null;
  if (!sync) return null;
  if (!counts) {
    return (
      <Callout kind="warn" icon="clock">
        No customer sync has run yet. Run one to read Autotask's active customers.
      </Callout>
    );
  }
  const addsUp = counts.owned + counts.waiting === counts.autotaskActive;
  const notCounted = (customers ?? []).filter((c) => c.companyType !== 1).length;
  const run = sync.lastRun;
  return (
    <>
      <div className="eq" data-testid="customers-counts">
        <div>
          <span className="n" data-testid="count-autotask">{counts.autotaskActive.toLocaleString("en-US")}</span>
          <span className="l">active customers in Autotask</span>
        </div>
        <div className="op" aria-hidden="true">=</div>
        <div>
          <span className="n" data-testid="count-owned">{counts.owned.toLocaleString("en-US")}</span>
          <span className="l">under a dealer</span>
        </div>
        <div className="op" aria-hidden="true">+</div>
        <div>
          <span className="n" data-testid="count-waiting">{counts.waiting.toLocaleString("en-US")}</span>
          <Link className="l" to="/customers?tab=review">
            waiting for a dealer
          </Link>
        </div>
      </div>
      <div className="eq-foot">
        {addsUp ? (
          <Chip kind="ok" icon="check" testId="counts-check" status="adds-up">
            Adds up
          </Chip>
        ) : (
          <Chip kind="stop" icon="triangle-alert" testId="counts-check" status="off">
            Off by {Math.abs(counts.autotaskActive - counts.owned - counts.waiting).toLocaleString("en-US")}
          </Chip>
        )}
        {sync.customers?.lastOkAt ? (
          <span>
            Last sync {stamp(sync.customers.lastOkAt)}
            {run ? ` \u00b7 ${run.trigger === "schedule" ? "Scheduled" : `Run by ${run.by ?? "Phoneware"}`}` : ""}
          </span>
        ) : null}
        <span>Reads Autotask and OneBill, writes only to Omni</span>
        {notCounted ? <span>Plus {plural(notCounted, "prospect or lead", "prospects and leads")} in Omni, not counted as customers</span> : null}
      </div>
      {sync.customers?.lastError ? (
        <Callout kind="stop" icon="triangle-alert">
          The last customer sync failed: {sync.customers.lastError}
        </Callout>
      ) : null}
    </>
  );
}

function CustomerList({
  customers,
  dealers,
  ops,
  emptyText,
  toReview,
}: {
  customers: CustomerSummary[] | null;
  dealers: Dealer[];
  ops: boolean;
  emptyText: string;
  /** Account suggestions per customer, for a reviewer (customer_accounts.review); null for anyone else. */
  toReview: Record<number, number> | null;
}): React.ReactElement {
  const [find, setFind] = useState("");
  const [dealerId, setDealerId] = useState("");
  const [type, setType] = useState<TypeFilter>("all");
  const [bill, setBill] = useState<BillingFilter>("any");
  const q = find.trim().toLowerCase();
  const list = [...(customers ?? [])]
    .filter((c) => !q || c.name.toLowerCase().includes(q) || String(c.autotaskCompanyId) === q || (c.accountNumber ?? "").toLowerCase().includes(q))
    .filter((c) => !dealerId || c.dealerId === dealerId)
    .filter((c) => (type === "all" ? true : type === "customers" ? c.companyType === 1 : c.companyType !== 1))
    .filter((c) => bill === "any" || c.billing.state === bill)
    .sort((a, b) => a.name.localeCompare(b.name));
  const dealerName = (id: string) => dealers.find((d) => d.id === id)?.name ?? "";
  const actives = (customers ?? []).filter((c) => c.companyType === 1 && c.isActive);
  const active = actives.length;
  const numbered = actives.filter((c) => Boolean(c.accountNumber?.trim())).length;
  const repName = useRepNames();

  return (
    <section className="card" data-testid="customers-list">
      <div className="filters">
        <label className="search grow">
          <Icon name="search" size={15} />
          <span className="sr">Find a customer or an account number</span>
          <input className="input" type="search" placeholder="Find a customer or an account number" data-testid="customers-find" value={find} onChange={(e) => setFind(e.target.value)} />
        </label>
        {ops ? (
          <select className="input" aria-label="Dealer" data-testid="customers-dealer" value={dealerId} onChange={(e) => setDealerId(e.target.value)}>
            <option value="">Every dealer</option>
            {[...dealers].sort((a, b) => a.name.localeCompare(b.name)).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        ) : null}
        <select className="input" aria-label="Type" data-testid="customers-type" value={type} onChange={(e) => setType(e.target.value as TypeFilter)}>
          <option value="all">Customers and prospects</option>
          <option value="customers">Customers</option>
          <option value="prospects">Prospects and leads</option>
        </select>
        <select className="input" aria-label="OneBill" data-testid="customers-billing" value={bill} onChange={(e) => setBill(e.target.value as BillingFilter)}>
          <option value="any">Any OneBill state</option>
          <option value="linked">Linked</option>
          <option value="unreviewed">Not reviewed</option>
          <option value="none">No account</option>
        </select>
      </div>
      {!customers ? (
        <div className="empty">Loading customers.</div>
      ) : customers.length === 0 ? (
        <div className="empty">{emptyText}</div>
      ) : list.length === 0 ? (
        <div className="empty">No customers match.</div>
      ) : (
        <div className="tablewrap">
          <table className="t t-cust st">
            <thead>
              <tr>
                <th>Customer</th>
                {ops ? <th>Dealer</th> : null}
                <th>Account number</th>
                <th>Type</th>
                <th>OneBill</th>
                <th>Accounts</th>
                {toReview ? <th>To review</th> : null}
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.autotaskCompanyId} data-customer={c.autotaskCompanyId} {...rowLink(`/customers/${c.autotaskCompanyId}`)}>
                  <td>
                    <Link to={`/customers/${c.autotaskCompanyId}`}>
                      <b>{c.name}</b>
                    </Link>
                    <span className="sub">{placeLine(c)}</span>
                    <MadeBy via={c.createdVia} person={c.createdBy ? repName(c.createdBy) : null} block testId={`customer-made-by-${c.autotaskCompanyId}`} />
                    {ops && !c.categoryCheck.matches && !c.categoryCheck.exception ? (
                      <span className="sub">
                        <Chip kind="warn" icon="triangle-alert" testId={`customer-differs-${c.autotaskCompanyId}`}>
                          Category differs
                        </Chip>
                      </span>
                    ) : null}
                  </td>
                  {ops ? <td data-label="Dealer">{dealerName(c.dealerId)}</td> : null}
                  <td data-label="Account number">
                    <AccountNumber number={c.accountNumber} testId={`customer-number-${c.autotaskCompanyId}`} />
                  </td>
                  <td data-label="Type">
                    <TypeTag type={c.companyType} />
                  </td>
                  <td data-label="OneBill">
                    <BillingChip billing={c.billing} testId={`customer-billing-${c.autotaskCompanyId}`} />
                  </td>
                  <td data-label="Accounts">
                    <AccountIcons products={c.accountProducts} testId={`customer-accounts-${c.autotaskCompanyId}`} />
                  </td>
                  {toReview ? (
                    <td data-label="To review">
                      {toReview[c.autotaskCompanyId] ? (
                        <Chip kind="warn" testId={`customer-to-review-${c.autotaskCompanyId}`}>
                          {toReview[c.autotaskCompanyId]} suggested
                        </Chip>
                      ) : (
                        <span className="muted">None</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {customers && customers.length ? (
        <div className="acct-foot">
          <AccountNumberLegend />
          <p className="card-note">
            The account number is the Autotask company Number, and OneBill bills under the same number.
            {ops ? ` ${numbered.toLocaleString("en-US")} of ${plural(active, "active customer")} in Omni have one.` : ""} Omni gives each new customer a PW number when it creates the prospect.
          </p>
        </div>
      ) : null}
    </section>
  );
}

/** A customer whose first suggestion is the OneBill account carrying its Autotask Number, under a name that doesn't agree. */
export interface NumberPair {
  c: CustomerSummary;
  cand: CustomerBillingCandidate;
}

interface SettledPair {
  customerId: number;
  name: string;
  accountNumber: string;
  accountName: string;
  how: "linked" | "apart";
}

/** The plan's number tier (3.2) leaves these unreviewed with `why: "number"` first. An account Phoneware kept apart is dismissed in review. */
export function numberPairs(customers: CustomerSummary[], review: BillingReviewItem[]): NumberPair[] {
  const apart = new Set(review.filter((r) => r.state === "dismissed").map((r) => r.accountNumber));
  const out: NumberPair[] = [];
  for (const c of customers) {
    if (c.billing.state !== "unreviewed") continue;
    const cand = c.billing.candidates[0];
    if (!cand || cand.why !== "number" || apart.has(cand.accountNumber)) continue;
    out.push({ c, cand });
  }
  return out.sort((a, b) => a.c.name.localeCompare(b.c.name));
}

type ReviewSheet =
  | { kind: "link"; categoryId: number; dealerId: string | null }
  | { kind: "create"; categoryId: number | null }
  | { kind: "billing"; customerId: number; accountNumber: string; accountName: string };

function ReviewTab({
  customers,
  dealers,
  sync,
  users,
  orders,
  reloadAll,
}: {
  customers: CustomerSummary[];
  dealers: Dealer[];
  sync: SyncStatus | null;
  users: LensUser[] | null;
  orders: Order[];
  reloadAll: () => Promise<void>;
}): React.ReactElement {
  const { api, me, can, writeScope } = useSession();
  const toast = useToast();
  // Every Phoneware tier reads the review; each decision belongs to the permission the server checks for it.
  const billingOk = writeScope("billing.accounts") === "any";
  const assignOk = can("customers.assign");
  const syncOk = can("sync.run");
  const dealersOk = writeScope("dealers.manage") === "any";
  const review = useLoad<CustomersReview>(() => api.customersReview(), []);
  const categories = useLoad<CategoryStatus[]>(() => api.autotaskCategories(), []);
  const [sheet, setSheet] = useState<ReviewSheet | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [settled, setSettled] = useState<Array<{ name: string; text: string }>>([]);
  const [pairsSettled, setPairsSettled] = useState<SettledPair[]>([]);
  const cats = categories.data ?? [];
  const cat = (id: number | null) => (id === null ? null : cats.find((c) => c.id === id) ?? null);
  const dealer = (id: string) => dealers.find((d) => d.id === id) ?? null;
  const holder = (categoryId: number | null) => (categoryId === null ? null : dealers.find((d) => categoryLinkOf(d)?.id === categoryId) ?? null);

  async function refresh(): Promise<void> {
    const [nextReview, nextCats] = await Promise.all([api.customersReview(), api.autotaskCategories(), reloadAll()]);
    review.set(nextReview);
    categories.set(nextCats);
  }

  async function decide(item: CustomersReview["categoryDiffers"][number], action: "keep" | "move"): Promise<void> {
    setBusy(`cat-${item.autotaskCompanyId}`);
    try {
      await api.decideCategory(item.autotaskCompanyId, { action, observedCategoryId: item.observedCategoryId });
      const from = dealer(item.dealerId);
      const link = from ? categoryLinkOf(from) : null;
      const to = holder(item.observedCategoryId);
      const text =
        action === "keep"
          ? link
            ? `Kept with ${item.dealerName} by ${me.user.name}. Omni wrote ${link.name} back to Autotask and read it back.`
            : `Kept with ${item.dealerName} by ${me.user.name} as a Phoneware exception.`
          : `Moved from ${item.dealerName} to ${to?.name ?? "the category's dealer"} by ${me.user.name}, following Autotask.`;
      setSettled((s) => [...s, { name: item.customerName, text }]);
      toast(text);
      await refresh();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function decideAccount(accountNumber: string, action: "dismiss" | "reopen", name: string): Promise<void> {
    setBusy(`ob-${accountNumber}`);
    try {
      await api.decideBillingReview(accountNumber, { action });
      toast(action === "dismiss" ? `Dismissed ${name}. Later syncs leave it alone.` : `Reopened ${name}.`);
      await refresh();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  /** A pair whose Autotask Number equals a OneBill account number while the names differ: link them, or keep them apart. */
  async function decidePair(pair: NumberPair, how: SettledPair["how"]): Promise<void> {
    const { c, cand } = pair;
    setBusy(`pair-${c.autotaskCompanyId}`);
    try {
      if (how === "linked") await api.setCustomerBilling(c.autotaskCompanyId, { action: "link", accountNumber: cand.accountNumber });
      else await api.decideBillingReview(cand.accountNumber, { action: "dismiss" });
      setPairsSettled((s) => [...s.filter((x) => x.customerId !== c.autotaskCompanyId), { customerId: c.autotaskCompanyId, name: c.name, accountNumber: cand.accountNumber, accountName: cand.name, how }]);
      toast(how === "linked" ? `Linked ${c.name} to OneBill ${cand.accountNumber}.` : `Kept ${c.name} apart from ${cand.name}.`);
      await refresh();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function undoPair(p: SettledPair): Promise<void> {
    setBusy(`pair-${p.customerId}`);
    try {
      if (p.how === "linked") await api.setCustomerBilling(p.customerId, { action: "reset_unreviewed" });
      else await api.decideBillingReview(p.accountNumber, { action: "reopen" });
      setPairsSettled((s) => s.filter((x) => x.customerId !== p.customerId));
      toast(p.how === "linked" ? `${p.name} is back to not reviewed. The next sync suggests ${p.accountNumber} again.` : `Reopened ${p.name} and ${p.accountName}.`);
      await refresh();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function syncNow(): Promise<void> {
    setBusy("sync");
    try {
      const run = await api.runSync({ steps: ["customers", "billing"] });
      const step = run.steps.customers;
      toast(step?.outcome === "failed" ? `The sync failed: ${step.error ?? "Autotask did not answer."}` : `Synced. ${plural(step?.wrote.owned ?? 0, "customer")} moved under a dealer.`);
      await refresh();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  if (review.error) {
    return (
      <Callout kind="stop" icon="triangle-alert">
        {review.error.message}
      </Callout>
    );
  }
  const data = review.data;
  if (!data) return <div className="empty">Loading what needs review.</div>;

  const kept = customers.filter((c) => c.categoryCheck.exception);
  const groups: Array<{ categoryId: number | null; reason: CustomersReview["waitingCustomers"][number]["reason"]; names: string[] }> = [];
  for (const w of data.waitingCustomers) {
    const g = groups.find((x) => x.categoryId === w.categoryId);
    if (g) g.names.push(w.name);
    else groups.push({ categoryId: w.categoryId, reason: w.reason, names: [w.name] });
  }
  const open = data.billingReview.filter((r) => r.state === "open");
  const dismissed = data.billingReview.filter((r) => r.state === "dismissed");
  const counts = sync?.billing?.counts ?? null;
  const waitingBy: Record<string, number> = {};
  for (const g of groups) waitingBy[String(g.categoryId)] = g.names.length;
  const sheetCategory = sheet?.kind === "link" ? cat(sheet.categoryId) : null;
  const sheetCustomer = sheet?.kind === "billing" ? customers.find((c) => c.autotaskCompanyId === sheet.customerId) ?? null : null;
  const pairs = numberPairs(customers, data.billingReview).filter((p) => !pairsSettled.some((s) => s.customerId === p.c.autotaskCompanyId));
  const linkedByNumber = customers.filter((c) => c.billing.state === "linked" && c.billing.via === "account_number").length;
  const phoneOf = (accountNumber: string) => data.billingReview.find((r) => r.accountNumber === accountNumber)?.phone ?? null;

  return (
    <div className="stack" data-testid="customers-review">
      <section className="card" data-testid="review-numbers">
        <div className="card-h wrap">
          <h2>Same number, names differ</h2>
          {pairs.length ? (
            <Chip kind="warn" icon="hash" testId="review-numbers-count" status="open">
              {plural(pairs.length, "pair")} to check
            </Chip>
          ) : (
            <Chip kind="ok" icon="check" testId="review-numbers-count" status="checked">
              All checked
            </Chip>
          )}
          <span className="note">From the number match on the last sync</span>
        </div>
        <p className="ns-note">
          {linkedByNumber ? `The last sync linked ${plural(linkedByNumber, "customer")} whose Autotask Number equals a OneBill account number and whose names agree. ` : ""}
          These share a number, but the names don{"\u2019"}t agree, so a person decides. Each one{"\u2019"}s first suggestion is this account.
        </p>
        <ul className="pairs">
          {pairsSettled.map((p) => (
            <li key={`done-${p.customerId}`} data-pair-settled={p.customerId}>
              <div className="done-l">
                <Chip kind={p.how === "linked" ? "ok" : "quiet"} icon={p.how === "linked" ? "link" : "x"}>
                  {p.how === "linked" ? "Linked" : "Not the same"}
                </Chip>{" "}
                <b>{p.name}</b>{" "}
                <span className="muted small">
                  {p.how === "linked" ? `now bills on ${p.accountNumber}, linked by ${me.user.name} just now.` : `and ${p.accountName} stay apart. Later syncs leave the pair alone.`}
                </span>{" "}
                {billingOk ? (
                  <button className="link" type="button" data-testid={`pair-undo-${p.customerId}`} disabled={busy !== null} onClick={() => void undoPair(p)}>
                    Undo
                  </button>
                ) : null}
              </div>
            </li>
          ))}
          {pairs.map((p) => {
            const phone = phoneOf(p.cand.accountNumber);
            return (
              <li key={p.c.autotaskCompanyId} data-pair={p.c.autotaskCompanyId}>
                <div className="side-l">
                  <span className="who">In Autotask</span>
                  <b>
                    <Link to={`/customers/${p.c.autotaskCompanyId}`}>{p.c.name}</Link>
                  </b>
                  <span className="row-m">{[dealer(p.c.dealerId)?.name, `Company ${p.c.autotaskCompanyId}`, `Number ${p.c.accountNumber ?? p.cand.accountNumber}`].filter(Boolean).join(" \u00b7 ")}</span>
                </div>
                <span className="same" title="Same number">
                  <Icon name="hash" size={14} />
                </span>
                <div className="side-r">
                  <span className="who">In OneBill</span>
                  <b>{p.cand.name}</b>
                  <span className="row-m">{[p.cand.accountNumber, phone].filter(Boolean).join(" \u00b7 ")}</span>
                </div>
                <div className="acts">
                  {billingOk ? (
                  <>
                  <button className="btn small primary" type="button" data-testid={`pair-link-${p.c.autotaskCompanyId}`} disabled={busy !== null} onClick={() => void decidePair(p, "linked")}>
                    <Icon name="link" size={13} />
                    Link
                  </button>
                  <button className="btn small" type="button" data-testid={`pair-apart-${p.c.autotaskCompanyId}`} disabled={busy !== null} onClick={() => void decidePair(p, "apart")}>
                    Not the same
                  </button>
                  </>
                  ) : (
                    <span className="row-m">Billing ops decide</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {pairs.length === 0 && pairsSettled.length === 0 ? <div className="empty">No pairs to check. Every number match the sync found agrees on the name.</div> : null}
      </section>
      <section className="card" data-testid="review-differs">
        <div className="card-h">
          <h2>Category differs in Autotask</h2>
          <span className="note">Someone changed the category in Autotask. Omni didn't follow.</span>
        </div>
        {data.categoryDiffers.length === 0 ? (
          <div className="empty">Nothing differs. Every customer's Autotask category matches its dealer.</div>
        ) : (
          data.categoryDiffers.map((item) => {
            const from = dealer(item.dealerId);
            const link = from ? categoryLinkOf(from) : null;
            const to = holder(item.observedCategoryId);
            const now = item.observedCategoryName ?? (item.observedCategoryId === null ? "no category" : `Category ${item.observedCategoryId}`);
            const keepText = link ? `Keep writes ${link.name} back to Autotask.` : `Keep records it as a Phoneware exception.`;
            return (
              <div className="rv" key={item.autotaskCompanyId} data-differs={item.autotaskCompanyId}>
                <div className="rv-main">
                  <b>
                    <Link to={`/customers/${item.autotaskCompanyId}`}>{item.customerName}</Link>
                  </b>
                  <span className="row-m">
                    Company {item.autotaskCompanyId}
                    {sync?.customers?.lastOkAt ? ` \u00b7 seen at the ${stamp(sync.customers.lastOkAt)} sync` : ""}
                  </span>
                  <dl className="vs">
                    <dt>Omni</dt>
                    <dd>
                      {item.dealerName}
                      {link ? ` (${link.name})` : ", which has no category"}
                    </dd>
                    <dt>Autotask now</dt>
                    <dd>
                      {now}
                      {to ? "" : ", which has no dealer"}
                    </dd>
                  </dl>
                </div>
                <div className="rv-acts">
                  <div className="btnrow" hidden={!assignOk}>
                    <button className="btn small" type="button" data-testid={`keep-${item.autotaskCompanyId}`} disabled={busy !== null} onClick={() => void decide(item, "keep")}>
                      Keep Omni's
                    </button>
                    <button className="btn small" type="button" data-testid={`take-${item.autotaskCompanyId}`} disabled={busy !== null || !to} onClick={() => void decide(item, "move")}>
                      Take Autotask's
                    </button>
                  </div>
                  <span className="row-m">{to ? `${keepText} Take moves it to ${to.name}.` : `${keepText} Take needs a dealer for ${now} first.`}</span>
                </div>
              </div>
            );
          })
        )}
        {settled.length ? (
          <>
            <p className="sec-h">Settled this session</p>
            {settled.map((s, i) => (
              <div className="rv" key={i}>
                <div className="rv-main">
                  <b>{s.name}</b>
                  <span className="row-m">{s.text}</span>
                </div>
              </div>
            ))}
          </>
        ) : null}
        <p className="sec-h">Kept as exceptions</p>
        {kept.length === 0 ? (
          <p className="field-note">None. Ops keep a customer as an exception when its dealer has no category to write back.</p>
        ) : (
          kept.map((c) => {
            const d = dealer(c.dealerId);
            const link = d ? categoryLinkOf(d) : null;
            return (
              <div className="rv" key={c.autotaskCompanyId} data-kept={c.autotaskCompanyId}>
                <div className="rv-main">
                  <b>
                    <Link to={`/customers/${c.autotaskCompanyId}`}>{c.name}</Link>
                  </b>
                  <dl className="vs">
                    <dt>Omni</dt>
                    <dd>
                      {d?.name ?? c.dealerId}
                      {link ? ` (${link.name})` : ", which has no category"}
                    </dd>
                    <dt>Autotask</dt>
                    <dd>{c.categoryCheck.autotaskCategoryName ?? (c.categoryCheck.autotaskCategoryId === null ? "No category" : `Category ${c.categoryCheck.autotaskCategoryId}`)}</dd>
                  </dl>
                </div>
                <div className="rv-acts">
                  <Chip kind="quiet" icon="lock">
                    Kept by Phoneware
                  </Chip>
                </div>
              </div>
            );
          })
        )}
      </section>

      <section className="card" data-testid="review-waiting">
        <div className="card-h">
          <h2>Not under a dealer</h2>
          <span className="note">
            {plural(data.waitingCustomers.length, "customer")} in {plural(groups.length, "category", "categories")}
          </span>
        </div>
        {groups.length === 0 ? (
          <div className="empty">Every active customer is under a dealer.</div>
        ) : (
          groups.map((g) => {
            const c = cat(g.categoryId);
            const d = holder(g.categoryId);
            const suggestion = c ? suggestDealer(c, dealers) : null;
            const reason = d ? "waiting for the next sync" : g.reason === "no_category" ? "no category in Autotask" : g.reason === "inactive_category" ? "category is inactive in Autotask" : "category has no dealer";
            const shownNames = g.names.slice(0, 2);
            return (
              <div className="rv" key={String(g.categoryId)} data-waiting={String(g.categoryId)}>
                <div className="rv-main">
                  <b>{c ? <CategoryName name={c.name} nickname={c.nickname} /> : g.categoryId === null ? "No category" : `Category ${g.categoryId}`}</b>
                  <span className="row-m">
                    {plural(g.names.length, "customer")} {"\u00b7"} {reason}
                  </span>
                  <span className="row-m">
                    {shownNames.join(", ")}
                    {g.names.length > shownNames.length ? ` and ${g.names.length - shownNames.length} more` : ""}
                  </span>
                </div>
                <div className="rv-acts">
                  {d ? (
                    <>
                      <div className="btnrow" hidden={!syncOk}>
                        <button className="btn small" type="button" disabled={busy !== null} onClick={() => void syncNow()}>
                          <Icon name="refresh-cw" size={13} />
                          Sync now
                        </button>
                      </div>
                      <span className="row-m">Linked to {d.name}. They move on the next sync.</span>
                    </>
                  ) : !c || !c.active ? (
                    <span className="row-m">Set an active category in Autotask; the next sync files them.</span>
                  ) : !dealersOk ? (
                    <span className="row-m">A Super admin links the category to a dealer.</span>
                  ) : suggestion ? (
                    <div className="btnrow">
                      <button className="btn small primary" type="button" data-testid={`review-link-${c.id}`} onClick={() => setSheet({ kind: "link", categoryId: c.id, dealerId: suggestion.dealer.id })}>
                        Review link to {suggestion.dealer.name}
                      </button>
                    </div>
                  ) : (
                    <div className="btnrow">
                      <button className="btn small primary" type="button" data-testid={`review-create-${c.id}`} onClick={() => setSheet({ kind: "create", categoryId: c.id })}>
                        Create dealer
                      </button>
                      <button className="btn small" type="button" data-testid={`review-linkdealer-${c.id}`} onClick={() => setSheet({ kind: "link", categoryId: c.id, dealerId: null })}>
                        Link a dealer
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </section>

      <section className="card" data-testid="review-onebill">
        <div className="card-h">
          <h2>OneBill accounts</h2>
          <span className="note">Accounts no customer could be matched to for certain</span>
        </div>
        {counts ? (
          <div className="obsum">
            <Chip kind="quiet">{counts.onebillAccounts.toLocaleString("en-US")} in OneBill</Chip>
            <Chip kind="ok" icon="check">
              {counts.linked.toLocaleString("en-US")} linked
            </Chip>
            <Chip kind="warn" icon="triangle-alert">
              {counts.open.toLocaleString("en-US")} open
            </Chip>
            <Chip kind="quiet">{counts.dismissed.toLocaleString("en-US")} dismissed</Chip>
          </div>
        ) : null}
        {open.length === 0 ? <div className="empty">No OneBill account is waiting for a customer.</div> : null}
        {[...open, ...dismissed].map((r, i) => (
          <React.Fragment key={r.accountNumber}>
            {r.state === "dismissed" && (i === 0 || open.length === i) ? <p className="sec-h">Dismissed</p> : null}
            <div className="rv" data-account={r.accountNumber}>
              <div className="rv-main">
                <b>{r.accountName}</b>
                <span className="row-m mono">
                  {r.accountNumber}
                  {r.phone ? ` \u00b7 ${r.phone}` : ""}
                </span>
                {r.state === "open" ? (
                  r.candidates.length && billingOk ? (
                    <div className="cands">
                      {r.candidates.map((x) => (
                        <button
                          key={x.autotaskCompanyId}
                          className="btn small"
                          type="button"
                          data-testid={`ob-pick-${r.accountNumber}-${x.autotaskCompanyId}`}
                          onClick={() => setSheet({ kind: "billing", customerId: x.autotaskCompanyId, accountNumber: r.accountNumber, accountName: r.accountName })}
                        >
                          <Icon name="link" size={13} />
                          {x.name} {"\u00b7"} {whyText(x)}
                        </button>
                      ))}
                    </div>
                  ) : r.candidates.length ? (
                    <span className="row-m">{plural(r.candidates.length, "possible customer")}. Billing ops link it.</span>
                  ) : (
                    <span className="row-m">No customer scores 50% on name, and none shares its phone.</span>
                  )
                ) : (
                  <span className="row-m">
                    Dismissed by {personName(users, r.decidedBy)}
                    {r.decidedAt ? ` \u00b7 ${stamp(r.decidedAt)}` : ""}
                  </span>
                )}
              </div>
              <div className="rv-acts">
                <div className="btnrow" hidden={!billingOk}>
                  {r.state === "open" ? (
                    <button className="btn small" type="button" data-testid={`ob-dismiss-${r.accountNumber}`} disabled={busy !== null} onClick={() => void decideAccount(r.accountNumber, "dismiss", r.accountName)}>
                      Dismiss
                    </button>
                  ) : (
                    <button className="btn small" type="button" data-testid={`ob-reopen-${r.accountNumber}`} disabled={busy !== null} onClick={() => void decideAccount(r.accountNumber, "reopen", r.accountName)}>
                      Reopen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}
      </section>

      {sheet?.kind === "link" && sheetCategory ? (
        <LinkCategorySheet
          category={sheetCategory}
          dealers={dealers}
          users={users}
          waiting={waitingBy[String(sheetCategory.id)] ?? 0}
          dealerId={sheet.dealerId}
          reload={async () => {
            const [nextDealers, nextCats] = await Promise.all([api.dealers(), api.autotaskCategories()]);
            categories.set(nextCats);
            await reloadAll();
            return { dealers: nextDealers, categories: nextCats };
          }}
          onClose={() => setSheet(null)}
        />
      ) : null}
      {sheet?.kind === "create" ? (
        <CreateDealerSheet categories={cats} waiting={waitingBy} categoryId={sheet.categoryId} reload={refresh} onClose={() => setSheet(null)} />
      ) : null}
      {sheet?.kind === "billing" && sheetCustomer ? (
        <BillingSheet
          customer={sheetCustomer}
          everyone={customers}
          preselect={{ accountNumber: sheet.accountNumber, name: sheet.accountName }}
          held={orders.find((o) => o.customerId === sheetCustomer.autotaskCompanyId && o.onebill.state === "failed") ?? null}
          onDone={() => void refresh()}
          onClose={() => setSheet(null)}
        />
      ) : null}
    </div>
  );
}

export function Customers(): React.ReactElement {
  const { me, api, can, everyDealer } = useSession();
  const { query } = useLocation();
  const toast = useToast();
  const ops = everyDealer;
  const tab = ops && query.get("tab") === "review" ? "review" : "list";
  useTitle(tab === "review" ? "Customers to review" : "Customers");
  const customers = useLoad<CustomerSummary[]>(() => api.customers(), []);
  const dealers = useLoad<Dealer[]>(() => (ops ? api.dealers() : Promise.resolve([])), [ops]);
  const sync = useLoad<SyncStatus | null>(() => (ops ? api.syncStatus() : Promise.resolve(null)), [ops]);
  const users = useLoad<LensUser[]>(() => (ops ? api.users() : Promise.resolve([])), [ops]);
  const orders = useLoad<Order[]>(() => (ops ? api.orders() : Promise.resolve([])), [ops]);
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const canAdd = can("customers.write");
  // Billing ops and Super admins decide account suggestions; one read of the queue gives the list's counts and the Review tab.
  const reviewer = can("customer_accounts.review");
  const accountQueue = useLoad<AccountSuggestion[]>(() => (reviewer ? api.customerAccountSuggestions().then((r) => r.suggestions) : Promise.resolve([])), [reviewer]);
  const suggestionsBy: Record<number, number> = {};
  for (const s of accountQueue.data ?? []) suggestionsBy[s.customer.autotaskCompanyId] = (suggestionsBy[s.customer.autotaskCompanyId] ?? 0) + 1;
  const counts = sync.data?.customers?.counts;
  const toReview = (counts ? counts.differs + counts.waiting : 0) + (sync.data?.billing?.counts.open ?? 0) + (accountQueue.data?.length ?? 0);
  const mine = customers.data?.length ?? 0;
  const link = me.dealer ? categoryLinkOf(me.dealer) : null;

  async function reloadAll(): Promise<void> {
    const [nextCustomers, nextDealers, nextSync] = await Promise.all([api.customers(), api.dealers(), api.syncStatus()]);
    customers.set(nextCustomers);
    dealers.set(nextDealers);
    sync.set(nextSync);
  }

  async function syncNow(): Promise<void> {
    setSyncing(true);
    try {
      const run = await api.runSync({ steps: ["customers", "billing"] });
      const step = run.steps.customers;
      await reloadAll();
      toast(step?.outcome === "failed" ? `The sync failed: ${step.error ?? "Autotask did not answer."}` : `Synced. ${plural(step?.wrote.owned ?? 0, "customer")} moved under a dealer.`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <>
      <Head
        title="Customers"
        sub={
          ops
            ? "Every dealer\u2019s customers, as Autotask has them, with the account number OneBill bills under. Omni decides which dealer owns each one."
            : customers.data
              ? `Your ${plural(mine, "customer")}, as Autotask has them, with the account number OneBill bills under.`
              : `The customers ${me.dealer?.name ?? "your dealer"} owns in Autotask.`
        }
        actions={
          <>
            {can("sync.run") ? (
              <button className="btn" type="button" data-testid="customers-sync" disabled={syncing} onClick={() => void syncNow()}>
                <Icon name="refresh-cw" size={15} />
                {syncing ? "Syncing" : "Sync now"}
              </button>
            ) : null}
            {canAdd ? (
              <button className="btn primary" type="button" data-testid="customers-add" onClick={() => setAdding(true)}>
                <Icon name="user-plus" size={15} />
                Add a customer
              </button>
            ) : null}
          </>
        }
      />
      {customers.error ? (
        <Callout kind="stop" icon="triangle-alert">
          {customers.error.message}
        </Callout>
      ) : null}
      {ops ? <CountsStrip sync={sync.data} customers={customers.data} /> : null}
      {ops ? (
        <nav className="tabs" aria-label="Customers views">
          <Link to="/customers" aria-current={tab === "list" ? "page" : undefined} data-testid="tab-list">
            Customers
          </Link>
          <Link to="/customers?tab=review" aria-current={tab === "review" ? "page" : undefined} data-testid="tab-review">
            Review
            {toReview ? <i className="tdot" title={`${toReview} to review`} /> : null}
          </Link>
        </nav>
      ) : null}
      {tab === "review" ? (
        <div className="stack">
          {reviewer ? <AccountsToConfirm queue={accountQueue} customers={customers.data ?? []} /> : null}
          <ReviewTab customers={customers.data ?? []} dealers={dealers.data ?? []} sync={sync.data} users={users.data} orders={orders.data ?? []} reloadAll={reloadAll} />
        </div>
      ) : (
        <CustomerList
          customers={customers.data}
          dealers={dealers.data ?? []}
          ops={ops}
          emptyText={
            ops
              ? "No customers yet. The sync files each Autotask customer under its category's dealer."
              : link
                ? "No customers yet. Add one, or ask Phoneware to assign an existing Autotask company to you."
                : `No customers yet. Phoneware links ${me.dealer?.name ?? "your dealer"} to its Autotask category first; your customers show up after that.`
          }
          toReview={reviewer ? suggestionsBy : null}
        />
      )}
      {adding ? <AddCustomerSheet onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function BillingCard({
  c,
  ops,
  reviews,
  held,
  everyone,
  users,
  onReview,
}: {
  c: CustomerSummary;
  ops: boolean;
  /** Billing ops and Super admins: the buttons that open the OneBill account review. */
  reviews: boolean;
  held: Order | null;
  everyone: CustomerSummary[];
  users: (email: string) => string;
  onReview: () => void;
}): React.ReactElement {
  const b = c.billing;
  let body: React.ReactNode;
  if (b.state === "linked") {
    const also = everyone.filter((x) => x.autotaskCompanyId !== c.autotaskCompanyId && x.billing.state === "linked" && x.billing.accountNumber === b.accountNumber);
    body = (
      <>
        <div className="bill">
          <span className="mark">
            <Icon name="check" size={16} />
          </span>
          <div>
            <b className="mono" data-testid="customer-onebill-account">{b.accountNumber}</b>
            <span>Active in OneBill</span>
            <span>
              {BILL_VIA[b.via]}
              {b.via === "ops" && b.by ? ` \u00b7 ${users(b.by)}, ${stamp(b.at)}` : ""}
            </span>
            {also.length ? <span>Also bills {also.map((x) => x.name).join(", ")}</span> : null}
          </div>
        </div>
        {reviews ? (
          <div className="btnrow">
            <button className="btn small" type="button" data-testid="customer-billing-open" onClick={onReview}>
              Change account
            </button>
          </div>
        ) : null}
      </>
    );
  } else if (b.state === "none") {
    body = (
      <>
        <div className="bill none">
          <span className="mark">
            <Icon name="receipt" size={16} />
          </span>
          <div>
            <b>No OneBill account</b>
            <span>{b.via === "created_in_omni" ? `Added in Omni by ${b.by ? users(b.by) : "Omni"}, ${stamp(b.at)}` : `Confirmed by ${b.by ? users(b.by) : "Phoneware"}, ${stamp(b.at)}`}</span>
            <span data-testid="customer-onebill-next">
              {c.accountNumber?.trim()
                ? `Its first order opens one as ${c.accountNumber.trim()} when billing is approved, from the Autotask address and billing contact.`
                : "Its first order creates one from the Autotask address and billing contact."}
            </span>
          </div>
        </div>
        {reviews ? (
          <div className="btnrow">
            <button className="btn small" type="button" data-testid="customer-billing-open" onClick={onReview}>
              Link an account instead
            </button>
          </div>
        ) : null}
      </>
    );
  } else {
    body = (
      <>
        <div className="bill open">
          <span className="mark">
            <Icon name="triangle-alert" size={16} />
          </span>
          <div>
            <b>No certain match</b>
            <span>
              Omni found no account that matches one to one. {b.candidates.length ? `${plural(b.candidates.length, "suggestion")} below.` : "No suggestions."}
            </span>
          </div>
        </div>
        {b.candidates.length ? (
          <ul className="contacts">
            {b.candidates.map((x) => {
              const bills = everyone.find((y) => y.autotaskCompanyId !== c.autotaskCompanyId && y.billing.state === "linked" && y.billing.accountNumber === x.accountNumber);
              return (
                <li key={x.accountNumber}>
                  <b>{x.name}</b>
                  <span className="mono">{x.accountNumber}</span> {"\u00b7"} {whyText(x)}
                  {bills ? ` \u00b7 bills ${bills.name}` : ""}
                </li>
              );
            })}
          </ul>
        ) : null}
        <div className="callout warn hold-note">
          <Icon name="clock" size={15} />
          <p>
            {ops
              ? "Orders for this customer hold until someone links its OneBill account or confirms it has none."
              : "Phoneware is reviewing this customer's OneBill account. Orders hold until that's done."}
            {held ? (
              <>
                {" "}
                <Link to={`/orders/${encodeURIComponent(held.id)}`}>{held.number}</Link> is holding.
              </>
            ) : null}
          </p>
        </div>
        {reviews ? (
          <div className="btnrow">
            <button className="btn small primary" type="button" data-testid="customer-billing-open" onClick={onReview}>
              Review OneBill account
            </button>
          </div>
        ) : null}
      </>
    );
  }
  return (
    <section className={b.state === "unreviewed" ? "card attn" : "card"} id="billing" data-testid="customer-billing-card">
      <div className="card-h">
        <h2>OneBill account</h2>
        <BillingChip billing={b} testId="customer-billing-state" />
      </div>
      {body}
    </section>
  );
}

/** Where the account number came from, and what OneBill does with it. */
export function numberNote(c: CustomerSummary): string {
  const n = c.accountNumber?.trim() ?? "";
  if (!n) return "Omni gives it a PW number when its first order's billing is approved, and OneBill opens the account under it.";
  const source = accountNumberSource(n);
  const from = source === "omni" ? "A PW number Omni gave this customer." : source === "rpp" ? "A number from RPP." : "The Autotask company Number.";
  const b = c.billing;
  if (b.state === "linked") return b.accountNumber === n ? `${from} OneBill bills under the same number.` : `${from} OneBill bills it under ${b.accountNumber}.`;
  return `${from} OneBill opens the account under it.`;
}

export function CustomerScreen({ id }: { id: number }): React.ReactElement {
  const session = useSession();
  const { me, api, can, everyDealer, writeScope, canWrite, seesPrices } = session;
  const { query } = useLocation();
  const ops = everyDealer;
  // Billing ops and Super admins review OneBill accounts for any dealer's customer.
  const billingOk = writeScope("billing.accounts") === "any";
  const viewer = orderViewer(session);
  const loaded = useLoad<CustomerV2>(() => api.customer(id), [id]);
  const everyone = useLoad<CustomerSummary[]>(() => (ops ? api.customers() : Promise.resolve([])), [ops]);
  const quotes = useLoad<Quote[]>(() => (seesPrices ? api.quotes() : Promise.resolve([])), [seesPrices]);
  const orders = useLoad<Order[]>(() => api.orders(), []);
  const dealers = useLoad<Dealer[]>(() => (ops ? api.dealers() : Promise.resolve([])), [ops]);
  const owners = useLoad<AutotaskOwner[]>(() => (ops ? api.autotaskOwners() : Promise.resolve([])), [ops]);
  const categories = useLoad<CategoryStatus[]>(() => (ops ? api.autotaskCategories() : Promise.resolve([])), [ops]);
  const repName = useRepNames();
  const [assigning, setAssigning] = useState(false);
  const [billing, setBilling] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [addedContact, setAddedContact] = useState<number | null>(null);
  const toast = useToast();
  const c = loaded.data;
  useTitle(c ? c.name : "Customer");

  // Opened from a held order's "Review OneBill account": go straight to the sheet.
  const wantsBilling = billingOk && query.get("billing") === "review";
  useEffect(() => {
    if (wantsBilling && c) setBilling(true);
  }, [wantsBilling, c?.autotaskCompanyId]);

  if (!c) {
    if (isNotYours(loaded.error)) return <NotYours kind="customer" back="/customers" />;
    return (
      <>
        <Crumb to="/customers">Customers</Crumb>
        {loaded.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {loaded.error.message}
          </Callout>
        ) : (
          <div className="empty">Loading the customer.</div>
        )}
      </>
    );
  }

  const dealer = ops ? dealers.data?.find((d) => d.id === c.dealerId) ?? null : me.dealer;
  const isHouse = Boolean(dealer?.house);
  const link = dealer ? categoryLinkOf(dealer) : null;
  const categoryName =
    c.categoryId === null
      ? null
      : categories.data?.find((k) => k.id === c.categoryId)?.name ?? (link?.id === c.categoryId ? link.name : c.categoryCheck.autotaskCategoryId === c.categoryId ? c.categoryCheck.autotaskCategoryName : null) ?? `Category ${c.categoryId}`;
  const ownerPool: AutotaskOwner[] = [...(owners.data ?? []), ...(dealer?.autotaskOwner ? [dealer.autotaskOwner] : [])];
  const manager = resourceName(ownerPool, c.ownerResourceId);
  const parent = c.parentCompanyId !== null ? everyone.data?.find((x) => x.autotaskCompanyId === c.parentCompanyId) ?? null : null;
  const theirQuotes = (quotes.data ?? []).filter((q) => q.customerId === c.autotaskCompanyId).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const theirOrders = (orders.data ?? []).filter((o) => o.customerId === c.autotaskCompanyId);
  const held = theirOrders.find((o) => o.onebill.state === "failed") ?? null;
  // A dealer quotes its own customers; Phoneware quotes from here only for the house dealer (Sales at Phoneware, Super admins).
  const canQuote = canWrite("quotes.write", dealer) && (!ops || isHouse);
  const canContact = canWrite("customers.contacts", dealer);
  const differs = !c.categoryCheck.matches && !c.categoryCheck.exception;

  return (
    <>
      <Crumb to="/customers">Customers</Crumb>
      <Head
        title={
          <>
            {c.name}
            <MadeBy via={c.createdVia} person={c.createdBy ? repName(c.createdBy) : null} testId="customer-made-by" />
          </>
        }
        sub={[`Company ${c.autotaskCompanyId} in Autotask`, ops && dealer ? dealer.name : null, c.ownerEmail ? repName(c.ownerEmail) : null].filter(Boolean).join(" \u00b7 ")}
        actions={
          <>
            {can("customers.assign") ? (
              <button className="btn" type="button" data-testid="customer-assign" onClick={() => setAssigning(true)}>
                <Icon name="building-2" size={15} />
                Assign to a dealer
              </button>
            ) : null}
            {canContact ? (
              <button className={canQuote ? "btn" : "btn primary"} type="button" data-testid="customer-add-contact" onClick={() => setAddingContact(true)}>
                <Icon name="user-plus" size={15} />
                Add a contact
              </button>
            ) : null}
            {canQuote ? (
              <button className="btn primary" type="button" onClick={() => navigate(`/quotes/new?customer=${c.autotaskCompanyId}`)}>
                <Icon name="plus" size={15} />
                New quote
              </button>
            ) : null}
          </>
        }
      />
      <AccountsStrip customer={c} dealerName={dealer?.name ?? c.dealerId} everyone={everyone.data ?? []} />
      <div className="grid2 start cust">
        <div className="stack">
          <section className="card" data-testid="customer-autotask">
            <div className="card-h">
              <h2>In Autotask</h2>
              <span className="note">Read live from Autotask</span>
            </div>
            <div className="acct-block" data-testid="customer-account-number">
              <span className="lbl">Account number</span>
              <AccountNumber number={c.accountNumber} testId="customer-number" />
              <p className="row-m" data-testid="customer-number-note">{numberNote(c)}</p>
            </div>
            <dl className="facts">
              <div>
                <dt>Type</dt>
                <dd>
                  <TypeTag type={c.companyType} />
                </dd>
              </div>
              <div>
                <dt>Category</dt>
                <dd>{categoryName ?? <span className="muted">None</span>}</dd>
              </div>
              <div>
                <dt>Platform</dt>
                <dd>{c.platformName ?? <span className="muted">None yet</span>}</dd>
              </div>
              <div>
                <dt>Account manager in Autotask</dt>
                <dd>{manager ?? <span className="muted">None</span>}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{c.phone ?? <span className="muted">None</span>}</dd>
              </div>
              <div>
                <dt>Address</dt>
                <dd>
                  {c.address ? (
                    <>
                      {[c.address.line1, c.address.line2].filter(Boolean).join(", ")}
                      <br />
                      {`${c.address.city}, ${c.address.state} ${c.address.postalCode}`}
                    </>
                  ) : (
                    <span className="muted">None</span>
                  )}
                </dd>
              </div>
              {c.parentCompanyId !== null ? (
                <div>
                  <dt>Parent company</dt>
                  <dd>
                    <Link to={`/customers/${c.parentCompanyId}`}>{parent?.name ?? `Company ${c.parentCompanyId}`}</Link>
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>
          <section className="card" data-testid="customer-contacts-card">
            <div className="card-h">
              <h2>Contacts</h2>
              <span className="note">{plural(c.contacts.length, "contact")} in Autotask</span>
            </div>
            {c.contacts.length === 0 ? (
              <p className="ns-note">No contacts in Autotask.</p>
            ) : (
              <ul className="contacts people" data-testid="customer-contacts">
                {c.contacts.map((x) => (
                  <li key={x.autotaskContactId} data-contact={x.autotaskContactId}>
                    <Avatar name={x.name} />
                    <div>
                      <b>{x.name}</b>
                      <span className="row-m">{[x.title, x.email, x.phone].filter(Boolean).join(" \u00b7 ") || "No email or phone"}</span>
                    </div>
                    {addedContact === x.autotaskContactId ? (
                      <span className="new">
                        <Chip kind="ok" icon="check" testId="contact-added">
                          Added just now
                        </Chip>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {canContact ? (
              <div className="contacts-add">
                <button className="link" type="button" data-testid="customer-add-contact-link" onClick={() => setAddingContact(true)}>
                  <Icon name="user-plus" size={14} />
                  Add a contact
                </button>
              </div>
            ) : null}
          </section>
          {seesPrices ? (
            <section className="card">
              <div className="card-h">
                <h2>Quotes</h2>
              </div>
              {!quotes.data ? (
                <div className="empty">Loading quotes.</div>
              ) : theirQuotes.length === 0 ? (
                <div className="empty">No quotes yet.{canQuote ? " Start one with New quote." : ""}</div>
              ) : (
                <div className="tablewrap">
                  <table className="t">
                    <thead>
                      <tr>
                        <th>Quote</th>
                        <th className="hide-sm">Type</th>
                        <th>Status</th>
                        <th className="num">Monthly</th>
                      </tr>
                    </thead>
                    <tbody>
                      {theirQuotes.map((q) => (
                        <tr key={q.id} {...rowLink(`/quotes/${encodeURIComponent(q.id)}`)}>
                          <td className="mono">
                            <Link to={`/quotes/${encodeURIComponent(q.id)}`}>{q.number}</Link>
                          </td>
                          <td className="hide-sm">{QUOTE_TYPE[q.type]}</td>
                          <td>
                            <QuoteStatusChip status={q.status} />
                          </td>
                          <td className="num">{formatCents(quoteTotals(q.lines, dealer?.model ?? "agent").monthly)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ) : null}
        </div>
        <div className="stack">
          <BillingCard c={c} ops={ops} reviews={billingOk} held={held} everyone={everyone.data ?? []} users={repName} onReview={() => setBilling(true)} />
          <section className={differs ? "card attn" : "card"} data-testid="customer-dealer">
            <div className="card-h">
              <h2>Dealer in Omni</h2>
            </div>
            <p className="todo">
              <b>{dealer?.name ?? "Its dealer"}</b> owns this customer.
            </p>
            {differs ? (
              <div className="callout warn">
                <Icon name="triangle-alert" size={15} />
                <div>
                  <p>
                    Autotask now files it under {c.categoryCheck.autotaskCategoryName ?? "another category"}. Omni still has it with {dealer?.name ?? "its dealer"}.
                  </p>
                  {ops ? (
                    <div className="btnrow">
                      <Link className="btn small white" to="/customers?tab=review">
                        Settle it in Review
                      </Link>
                    </div>
                  ) : (
                    <p>Phoneware is checking which dealer it belongs to.</p>
                  )}
                </div>
              </div>
            ) : c.categoryCheck.exception ? (
              <p className="ns-note">
                Kept by Phoneware: {dealer?.name ?? "its dealer"} {link ? `holds ${link.name}` : "has no Autotask category"}, so Autotask files it under {c.categoryCheck.autotaskCategoryName ?? categoryName ?? "another category"}.
              </p>
            ) : link && categoryName ? (
              <p className="ns-note">
                Its Autotask category, {categoryName}, belongs to {dealer?.name}.
              </p>
            ) : null}
          </section>
          {ops ? <AccountSourcesCard /> : null}
          <section className="card">
            <div className="card-h">
              <h2>Orders</h2>
            </div>
            {!orders.data ? (
              <div className="empty">Loading orders.</div>
            ) : theirOrders.length === 0 ? (
              <div className="empty">No orders yet.</div>
            ) : (
              <ul className="rows">
                {theirOrders.map((o) => (
                  <li key={o.id}>
                    <div className="row" {...rowLink(`/orders/${encodeURIComponent(o.id)}`)}>
                      <span className="row-ic">
                        <Icon name="package" />
                      </span>
                      <div className="row-main">
                        <span className="row-t">{o.number}</span>
                        <span className="row-m">{stageLabel(o)}</span>
                      </div>
                      <div className="row-end">
                        <NextMove order={o} viewer={viewer} />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <RecordActivity kind="customer" id={String(c.autotaskCompanyId)} version={c} />
        </div>
      </div>
      {assigning ? (
        <AssignSheet
          customer={c}
          onClose={() => setAssigning(false)}
          onAssigned={() => {
            setAssigning(false);
            loaded.reload();
          }}
        />
      ) : null}
      {billing && billingOk ? (
        <BillingSheet
          customer={c}
          everyone={everyone.data ?? []}
          preselect={null}
          held={held}
          onDone={() => {
            loaded.reload();
            everyone.reload();
          }}
          onClose={() => {
            setBilling(false);
            if (wantsBilling) navigate(`/customers/${c.autotaskCompanyId}`, { replace: true });
          }}
        />
      ) : null}
      {addingContact ? (
        <AddContactSheet
          customer={c}
          onClose={() => setAddingContact(false)}
          onAdded={(contact) => {
            setAddingContact(false);
            setAddedContact(contact.autotaskContactId);
            loaded.set({ ...c, contacts: [...c.contacts.filter((x) => x.autotaskContactId !== contact.autotaskContactId), contact] });
            toast(`Added ${contact.name} to ${c.name} in Autotask.`);
          }}
        />
      ) : null}
    </>
  );
}

// ------------------------------------------------------------ a customer's accounts (mock M3 states 20 to 33, spec 3.6, #1777)

/** The strip's cells, as M3 names them (accounts.html:579-582). */
const STRIP_LABEL: Record<AccountProduct, string> = {
  edge: "Edge domains",
  texting: "Texting team",
  incontrol: "InControl group",
  billing: "Billing account",
  meta: "Meta business group",
  m365: "Microsoft 365 tenant",
};
/** One account of a product, as a review card names it. */
const ACCOUNT_KIND: Record<AccountProduct, string> = {
  edge: "Edge domain",
  texting: "Texting team",
  incontrol: "InControl group",
  billing: "Billing account",
  meta: "Meta business group",
  m365: "Microsoft 365 tenant",
};
/** Where the other side of a match lives. */
const OTHER_SIDE: Record<AccountProduct, string> = {
  edge: "In Edge",
  texting: "In Texting",
  incontrol: "In InControl",
  billing: "In OneBill",
  meta: "In Meta",
  m365: "At Microsoft",
};
/** Why confirming an account another customer holds is refused (#1645 contract 5.10). */
const ONE_CUSTOMER: Record<AccountProduct, string> = {
  edge: "A domain belongs to one customer.",
  texting: "A team belongs to one customer.",
  incontrol: "An organization belongs to one customer.",
  billing: "An account belongs to one customer.",
  meta: "A business group belongs to one customer.",
  m365: "A tenant belongs to one customer.",
};
/** Each kind of evidence, as the review card and the strip label it. */
const EVIDENCE_LABEL: Record<AccountEvidenceKind, string> = {
  billing_map: "Billing map",
  name_exact: "Name",
  name_near: "Name",
  contact_domain: "Contacts",
  classification: "Autotask",
  reseller_mismatch: "Reseller",
  team_key: "Team key",
  billing_sync: "Billing sync",
  verified_email: "Email",
  provider_subject: "SSO",
  login_link: "Linked",
  sign_in: "Sign-in",
  reviewed: "Reviewed",
};
/** The products the sync suggests, in the review filter's order (accounts.html:980). */
const SUGGESTED_PRODUCTS: ReadonlyArray<[AccountProduct, string]> = [
  ["edge", "Edge"],
  ["incontrol", "InControl"],
  ["meta", "Meta"],
  ["m365", "Microsoft 365"],
];

/** The server's words with typographic apostrophes, as the rest of the screen writes them. */
function prose(text: string): string {
  return text.replace(/'/g, "\u2019");
}
/** "Oct 12". */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** How a card or a cell shows one account: its name, whether it reads as a key, and what it is. */
function accountShown(product: AccountProduct, key: string, label: string): { value: string; mono: boolean; sub: string | null } {
  switch (product) {
    case "edge":
      return { value: key, mono: true, sub: "NS domain" };
    case "billing":
      return { value: key, mono: true, sub: "OneBill account" };
    case "m365": {
      // C4 labels a tenant with the contact domain that found it: "harborpointchiro.example (7c1e4b2a-...)".
      const m = /^(.+) \((.+)\)$/.exec(label);
      return m ? { value: m[1], mono: true, sub: `Tenant ${key}` } : { value: key, mono: true, sub: "Tenant" };
    }
    case "incontrol":
      return { value: label || key, mono: false, sub: `Organization ${key}` };
    case "meta":
      return { value: key, mono: true, sub: label && label !== key ? `Business group \u201c${label}\u201d` : "Business group" };
    case "texting":
      return { value: label || key, mono: false, sub: `Team ${key}` };
    default: {
      const unhandled: never = product;
      throw new Error(`Unknown product ${String(unhandled)}`);
    }
  }
}

/** "Billing map and Name 0.94": what a suggestion rests on, for the strip. */
function evidenceSummary(evidence: readonly AccountEvidence[]): string {
  const parts: string[] = [];
  for (const e of evidence) {
    if (e.kind === "reviewed") continue;
    const part = EVIDENCE_LABEL[e.kind] + (e.score != null ? ` ${e.score.toFixed(2)}` : "");
    if (!parts.includes(part)) parts.push(part);
  }
  return parts.join(" and ");
}

/** The accounts a reviewer rejected for this product, from the row's evidence: "Rejected winecountry, <iso>". */
function rejectionsOf(row: AccountRow): Array<{ key: string; at: string | null }> {
  const out: Array<{ key: string; at: string | null }> = [];
  for (const e of row.evidence) {
    const m = e.kind === "reviewed" ? /^Rejected (.+?)(?:, (\d{4}-\d\d-\d\dT\S+))?$/.exec(e.detail) : null;
    if (m) out.push({ key: m[1], at: m[2] ?? null });
  }
  return out;
}

/** Who stands behind a confirmed account: the reviewer (by name for Phoneware, as Phoneware for a dealer), or its source. */
function confirmedLine(product: AccountProduct, a: AccountRowAccount, kind: ViewerKind, nameOf: (email: string) => string): string {
  if (a.decidedBy && a.decidedAt) {
    switch (kind) {
      case "admin":
        return `Confirmed by ${nameOf(a.decidedBy)}, ${day(a.decidedAt)}`;
      case "dealer":
        return `Confirmed by Phoneware, ${day(a.decidedAt)}`;
      default: {
        const unhandled: never = kind;
        throw new Error(`Unknown viewer ${String(unhandled)}`);
      }
    }
  }
  if (product === "billing") return "From the OneBill billing sync";
  if (product === "texting") return "Linked with its team key";
  return evidenceSummary(a.evidence);
}

/** The customer a 409 `account_claimed` names as holding the account, or null for any other failure. */
function claimedBy(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body;
  if (!body || typeof body !== "object" || !("error" in body) || body.error !== "account_claimed" || !("heldBy" in body)) return null;
  const held = body.heldBy;
  if (!held || typeof held !== "object" || !("subjectId" in held)) return null;
  const id = Number(held.subjectId);
  return Number.isFinite(id) ? id : null;
}

/** One product's cell in a customer's Accounts strip. */
function StripCell({
  row,
  reviewer,
  unavailable,
  kind,
  nameOf,
  onReview,
}: {
  row: AccountRow;
  reviewer: boolean;
  unavailable: boolean;
  kind: ViewerKind;
  nameOf: (email: string) => string;
  onReview: (a: AccountRowAccount) => void;
}): React.ReactElement {
  const p = PRODUCT[row.product];
  const confirmed = row.accounts.filter((a) => a.state !== "suggested");
  const suggested = reviewer ? row.accounts.filter((a) => a.state === "suggested") : [];
  const rejected = rejectionsOf(row);
  let body: React.ReactNode = null;
  if (row.state === "no_source") {
    body = (
      <>
        <div className="val none">Not readable yet</div>
        <div className="meta">{row.evidence.map((e) => prose(e.detail)).join(" ")}</div>
      </>
    );
  } else if (confirmed.length) {
    body = confirmed.map((a) => {
      const s = accountShown(row.product, a.key, a.label);
      return (
        <React.Fragment key={a.matchId}>
          <div className={s.mono ? "val mono-key" : "val"}>{s.value}</div>
          <div className="meta">{confirmedLine(row.product, a, kind, nameOf)}</div>
        </React.Fragment>
      );
    });
  } else if (unavailable) {
    body = <div className="val none">Couldn&rsquo;t reach {p.name}</div>;
  } else if (!suggested.length && rejected.length) {
    body = (
      <>
        <div className="val none">None linked</div>
        <div className="meta">
          {rejected.map((r) => `Rejected ${r.key} ${r.at && new Date(r.at).toDateString() !== new Date().toDateString() ? `on ${day(r.at)}` : "today"}. Omni won\u2019t suggest it again.`).join(" ")}
        </div>
      </>
    );
  } else if (!suggested.length) {
    body = (
      <>
        <div className="val none">None found</div>
        <div className="meta">{row.evidence.map((e) => prose(e.detail)).join(" ")}</div>
      </>
    );
  }
  const tile = confirmed.length ? "ptile ok" : suggested.length ? "ptile warn" : "ptile quiet";
  return (
    <div className={suggested.length ? "cacc-cell attn" : "cacc-cell"} data-prod={row.product} data-state={row.state} data-unavailable={unavailable ? "true" : undefined} data-testid={`strip-${row.product}`}>
      <div className="lbl">
        <span className={tile}>
          <Icon name={p.icon} size={14} />
        </span>
        {STRIP_LABEL[row.product]}
      </div>
      {body}
      {unavailable ? (
        <div className="meta" data-testid={`unavailable-${row.product}`} title={row.evidence.map((e) => e.detail).join(" ")}>
          Omni couldn&rsquo;t reach {p.name} the last time it checked, so this may be out of date.
        </div>
      ) : null}
      {suggested.map((a) => {
        const s = accountShown(row.product, a.key, a.label);
        return (
          <div className="sug" key={a.matchId} data-testid={`strip-suggestion-${a.matchId}`}>
            <div className="sug-h">
              <Chip kind="warn" icon="circle-alert">
                Suggested
              </Chip>
              <button className="btn small white" type="button" data-testid={`review-one-${a.matchId}`} onClick={() => onReview(a)}>
                Review
              </button>
            </div>
            <div className={s.mono ? "val mono-key" : "val"}>{s.value}</div>
            <div className="meta">{evidenceSummary(a.evidence)}</div>
          </div>
        );
      })}
    </div>
  );
}

/** What a review card is about: a suggestion from the queue, or one the strip holds. */
type ReviewItem = Omit<AccountSuggestion, "createdAt"> & { createdAt: string | null };

/** One suggestion: the customer and the account side by side, why Omni suggests it, and Confirm or Reject. */
function ReviewCard({
  item,
  inSheet,
  customers,
  onDecided,
}: {
  item: ReviewItem;
  inSheet: boolean;
  customers: readonly CustomerSummary[];
  onDecided: (item: ReviewItem, record: AccountMatchRecord) => void;
}): React.ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [heldBy, setHeldBy] = useState<number | null>(null);
  const p = PRODUCT[item.product];
  const s = accountShown(item.product, item.key, item.label);
  const c = item.customer;
  const holderName = heldBy === null ? "" : customers.find((x) => x.autotaskCompanyId === heldBy)?.name ?? `Company ${heldBy}`;

  async function decide(decision: "confirm" | "reject"): Promise<void> {
    setBusy(true);
    try {
      const record = await api.decideCustomerAccount(item.matchId, decision);
      toast(decision === "confirm" ? `Confirmed. ${c.dealerName} now sees ${s.value} on ${c.name}.` : `Rejected. Omni won\u2019t suggest ${s.value} for ${c.name} again.`);
      onDecided(item, record);
    } catch (err) {
      const held = claimedBy(err);
      if (held === null) {
        toast(errorText(err));
      } else {
        setHeldBy(held);
        toast(`Omni didn\u2019t link ${s.value}. It already belongs to ${customers.find((x) => x.autotaskCompanyId === held)?.name ?? `Company ${held}`}.`);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="cacc-rq" data-sug={item.matchId} data-prod={item.product} data-testid={`suggestion-${item.matchId}`}>
      <div className="cacc-rq-h">
        <span className="ptile warn">
          <Icon name={p.icon} size={15} />
        </span>
        <b>{ACCOUNT_KIND[item.product]}</b>
        <Chip kind="quiet">{c.dealerName}</Chip>
        {item.createdAt ? <span className="when">Suggested {day(item.createdAt)}</span> : null}
      </div>
      <div className="cacc-pair">
        <div>
          <span className="side-l">In Omni</span>
          <b>{inSheet ? c.name : <Link to={`/customers/${c.autotaskCompanyId}`}>{c.name}</Link>}</b>
          <span className="sub">
            Company {c.autotaskCompanyId} &middot; {c.dealerName}
          </span>
        </div>
        <div className="mid">
          <Icon name="arrow-right-left" size={16} />
        </div>
        <div>
          <span className="side-l">{OTHER_SIDE[item.product]}</span>
          <b className={s.mono ? "mono-key" : undefined}>{s.value}</b>
          {s.sub ? <span className="sub">{s.sub}</span> : null}
        </div>
      </div>
      <p className="ev-h">Why Omni suggests it</p>
      <ul className="ev">
        {item.evidence.map((e, i) => (
          <li key={i}>
            <span className="k">{EVIDENCE_LABEL[e.kind]}</span>
            <span className="d">
              {prose(e.detail)}
              {e.score != null ? (
                <span className="cacc-score">
                  <span className="bar">
                    <i style={{ width: `${Math.round(Math.min(1, Math.max(0, e.score)) * 100)}%` }} />
                  </span>
                  {e.score.toFixed(2)}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {heldBy !== null ? (
        <Callout kind="stop" icon="ban">
          <b>Omni didn&rsquo;t link it.</b> {s.value} already belongs to {holderName}. {ONE_CUSTOMER[item.product]}
        </Callout>
      ) : null}
      <div className="btnrow">
        <button className="btn small primary" type="button" data-testid={`confirm-${item.matchId}`} disabled={busy} onClick={() => void decide("confirm")}>
          <Icon name="check" size={13} />
          Confirm
        </button>
        <button className="btn small white" type="button" data-testid={`reject-${item.matchId}`} disabled={busy} onClick={() => void decide("reject")}>
          <Icon name="x" size={13} />
          Reject
        </button>
        {heldBy !== null ? (
          <Link className="link" to={`/customers/${heldBy}`} data-testid={`open-holder-${item.matchId}`}>
            Open {holderName}
          </Link>
        ) : null}
      </div>
    </article>
  );
}

/**
 * A customer's account in each product Phoneware resells. The strip asks one question, may this viewer review
 * suggestions, and otherwise shows linked and matched accounts only, so a customer's own users can get it as is.
 */
function AccountsStrip({ customer, dealerName, everyone }: { customer: CustomerV2; dealerName: string; everyone: readonly CustomerSummary[] }): React.ReactElement {
  const { api, me, can } = useSession();
  const reviewer = can("customer_accounts.review");
  const kind = viewerKind(me.user);
  const nameOf = useRepNames();
  const id = customer.autotaskCompanyId;
  const accounts = useLoad<CustomerAccounts>(() => api.customerAccounts(id), [id]);
  const queue = useLoad<AccountSuggestion[]>(() => (reviewer ? api.customerAccountSuggestions().then((r) => r.suggestions) : Promise.resolve([])), [reviewer, id]);
  const [reviewing, setReviewing] = useState<ReviewItem | null>(null);
  const data = accounts.data;

  let body: React.ReactNode;
  if (!data) {
    body = accounts.error ? (
      <div className="cacc-empty">
        <Callout kind="stop" icon="triangle-alert">
          Omni couldn&rsquo;t load this customer&rsquo;s accounts: {accounts.error.message}
        </Callout>
      </div>
    ) : (
      <div className="cacc-empty">Loading accounts.</div>
    );
  } else {
    const cells = data.rows.filter((r) => reviewer || r.accounts.some((a) => a.state !== "suggested"));
    body = cells.length ? (
      <div className="cacc-strip">
        {cells.map((row) => (
          <StripCell
            key={row.product}
            row={row}
            reviewer={reviewer}
            unavailable={data.unavailable.includes(row.product)}
            kind={kind}
            nameOf={nameOf}
            onReview={(a) =>
              setReviewing(
                queue.data?.find((q) => q.matchId === a.matchId) ?? {
                  matchId: a.matchId,
                  customer: { autotaskCompanyId: id, name: customer.name, dealerId: customer.dealerId, dealerName },
                  product: row.product,
                  key: a.key,
                  label: a.label,
                  evidence: a.evidence,
                  createdAt: null,
                },
              )
            }
          />
        ))}
      </div>
    ) : (
      <div className="cacc-empty">Nothing confirmed yet.</div>
    );
  }
  const confirmedCount = data ? data.rows.filter((r) => r.accounts.some((a) => a.state !== "suggested")).length : 0;
  const toReview = data && reviewer ? data.rows.reduce((n, r) => n + r.accounts.filter((a) => a.state === "suggested").length, 0) : 0;

  return (
    <section className="card cacc" data-testid="accounts-strip" data-view={reviewer ? "review" : "confirmed"}>
      <div className="card-h">
        <h2>Accounts</h2>
        <span className="note" data-testid="accounts-strip-note">
          {reviewer ? `${confirmedCount} confirmed${toReview ? ` \u00b7 ${toReview} to review` : ""}` : "Confirmed by Phoneware"}
        </span>
      </div>
      {body}
      <div className="cacc-foot">
        <Icon name="info" size={14} />
        <span>{reviewer ? "Dealers see confirmed accounts only. Confirm or reject the rest here or in Customers, Review." : "Only accounts Phoneware has confirmed show here."}</span>
      </div>
      {reviewing ? (
        <Sheet
          title={`Review ${ACCOUNT_KIND[reviewing.product]}`}
          wide
          onClose={() => setReviewing(null)}
          foot={
            <button className="btn white" type="button" onClick={() => setReviewing(null)}>
              Close
            </button>
          }
        >
          <ReviewCard
            item={reviewing}
            inSheet
            customers={everyone}
            onDecided={() => {
              setReviewing(null);
              accounts.reload();
              queue.reload();
            }}
          />
        </Sheet>
      ) : null}
    </section>
  );
}

/** Where a customer's accounts come from, for Phoneware (accounts.html:932-935). */
function AccountSourcesCard(): React.ReactElement {
  return (
    <section className="card" data-testid="accounts-sources">
      <div className="card-h">
        <h2>Where its accounts come from</h2>
      </div>
      <dl className="facts" style={{ gridTemplateColumns: "1fr" }}>
        <div>
          <dt>Edge domains</dt>
          <dd>The billing map first, then exact and near-exact names. A domain belongs to one customer.</dd>
        </div>
        <div>
          <dt>Texting team</dt>
          <dd>The Texting link on a confirmed Edge domain.</dd>
        </div>
        <div>
          <dt>Microsoft 365</dt>
          <dd>Contact email domains, asked of Microsoft&rsquo;s public sign-in discovery. Omni suggests a tenant only when one of its domains spells the customer&rsquo;s name, or the name of a customer under the same Autotask parent.</dd>
        </div>
        <div>
          <dt>InControl and Meta</dt>
          <dd>Names, and for Meta the Autotask class UCaaS REI Meta.</dd>
        </div>
      </dl>
    </section>
  );
}

/** Accounts to confirm, the first section of the Customers review tab. customer_accounts.review only. */
function AccountsToConfirm({ queue, customers }: { queue: Loaded<AccountSuggestion[]>; customers: readonly CustomerSummary[] }): React.ReactElement {
  const { api, me } = useSession();
  const toast = useToast();
  const [filter, setFilter] = useState<AccountProduct | "all">("all");
  const [decided, setDecided] = useState<Array<{ item: ReviewItem; record: AccountMatchRecord }>>([]);
  const [busy, setBusy] = useState<string | null>(null);

  async function undo(d: { item: ReviewItem; record: AccountMatchRecord }): Promise<void> {
    setBusy(d.item.matchId);
    try {
      await api.undoCustomerAccount(d.item.matchId);
      setDecided((x) => x.filter((y) => y.item.matchId !== d.item.matchId));
      toast("Back in the queue.");
      queue.reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  const open = (queue.data ?? []).filter((q) => !decided.some((d) => d.item.matchId === q.matchId));
  const shown = filter === "all" ? open : open.filter((q) => q.product === filter);
  return (
    <>
      <section className="card" data-testid="review-accounts">
        <div className="card-h wrap">
          <h2>Accounts to confirm</h2>
          <span className="note">Confirmed accounts show to the customer&rsquo;s dealer. Rejected ones aren&rsquo;t suggested again.</span>
        </div>
        {queue.error && !queue.data ? (
          <Callout kind="stop" icon="triangle-alert">
            {queue.error.message}
          </Callout>
        ) : !queue.data ? (
          <div className="empty">Loading accounts to confirm.</div>
        ) : (
          <>
            <div className="rq-filter">
              <div className="seg" role="group" aria-label="Product">
                {([["all", "All"], ...SUGGESTED_PRODUCTS] as ReadonlyArray<[AccountProduct | "all", string]>).map(([k, t]) => (
                  <button key={k} type="button" data-testid={`review-filter-${k}`} aria-pressed={filter === k} onClick={() => setFilter(k)}>
                    {t}
                    <span className="n">{k === "all" ? open.length : open.filter((q) => q.product === k).length}</span>
                  </button>
                ))}
              </div>
            </div>
            {shown.length ? (
              shown.map((q) => (
                <ReviewCard
                  key={q.matchId}
                  item={q}
                  inSheet={false}
                  customers={customers}
                  onDecided={(item, record) => {
                    setDecided((x) => [...x, { item, record }]);
                    queue.reload();
                  }}
                />
              ))
            ) : (
              <div className="empty">{open.length ? "Nothing to confirm for this product." : "Nothing to confirm. Omni adds suggestions here after each sync."}</div>
            )}
          </>
        )}
        <p className="card-note">
          Texting teams come from the Texting link on a confirmed Edge domain, and Billing accounts from the billing sync, so neither lands here.
        </p>
      </section>
      {decided.length ? (
        <section className="card" data-testid="review-accounts-decided">
          <div className="card-h">
            <h2>Decided today</h2>
          </div>
          <div>
            {decided.map((d) => {
              const s = accountShown(d.item.product, d.item.key, d.item.label);
              const confirmed = d.record.state === "linked" || d.record.state === "matched";
              return (
                <div className="cacc-done" key={d.item.matchId} data-testid={`decided-${d.item.matchId}`}>
                  <span className={confirmed ? "ptile ok" : "ptile quiet"}>
                    <Icon name={PRODUCT[d.item.product].icon} size={15} />
                  </span>
                  <div className="cacc-done-m">
                    <b>
                      {s.value} &rarr; {d.item.customer.name}
                    </b>
                    <span>
                      {confirmed
                        ? `Confirmed by ${me.user.name} just now. ${d.item.customer.dealerName} sees it on the customer.`
                        : `Rejected by ${me.user.name} just now. Omni won\u2019t suggest it for this customer again.`}
                    </span>
                  </div>
                  <button className="link" type="button" data-testid={`undo-decided-${d.item.matchId}`} disabled={busy === d.item.matchId} onClick={() => void undo(d)}>
                    Undo
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
    </>
  );
}

/** The confirmed products of a customer, as the list's Accounts column draws them. */
function AccountIcons({ products, testId }: { products: readonly AccountProduct[]; testId: string }): React.ReactElement {
  if (!products.length) {
    return (
      <span className="muted" data-testid={testId}>
        None
      </span>
    );
  }
  return (
    <span className="mini-icons" data-testid={testId} aria-label={products.map((p) => STRIP_LABEL[p]).join(", ")}>
      {products.map((p) => (
        <span key={p} className="ptile ok" title={STRIP_LABEL[p]} data-prod={p}>
          <Icon name={PRODUCT[p].icon} size={12} />
        </span>
      ))}
    </span>
  );
}
